/**
 * Client-side DOCX import utility using mammoth.js and JSZip
 * Converts Word documents to HTML for TipTap editor with native comment and track changes support
 * 
 * Enhanced to preserve:
 * - Text alignment (left, center, right, justify)
 * - Underline formatting
 * - Hierarchical numbering (1, 1.1, 1.2, etc.)
 * - Text color
 * - Highlight/background color
 * - Font size variations
 * - Subscript and superscript
 * - Hyperlinks (via mammoth default)
 * - Line spacing (single, 1.5, double)
 * - Paragraph spacing (before/after)
 * - First line indent
 * - Paragraph indentation
 * - Table cell background colors
 * - Table cell borders
 * - Page breaks (as horizontal lines)
 */

import mammoth from "mammoth";
import JSZip from "jszip";
import {
  DEFAULT_FONT,
  resolveFontFamilyOrDefault,
} from "./fontMap";

/**
 * Result of importing a DOCX file
 */
export interface DocxImportResult {
  /** Converted HTML content with comment and track change marks */
  html: string;
  /** Warning messages from mammoth conversion */
  messages: string[];
  /** Whether the document contains track changes (revisions) */
  hasTrackChanges: boolean;
  /** Extracted comments for TipTap comment panel */
  comments: ImportedComment[];
  /** Extracted track changes for TipTap track changes panel */
  trackChanges: ImportedTrackChange[];
  /**
   * Comment marker metadata keyed by marker ID (cm1, cm2, …).
   * The HTML still contains Unicode markers (\uE000…\uE001) instead of
   * `<span data-comment-id>` elements.  Callers must run
   * `convertCommentMarkersToSpans(html, commentMarkerMeta)` **after** any
   * DOM round-trips (image upload, normalisation) to convert markers into
   * proper HTML spans.  This avoids browser DOMParser restructuring that
   * empties comment spans when they cross formatting boundaries.
   */
  commentMarkerMeta?: Map<string, CommentMarkerMeta>;
}

/**
 * Paragraph styling information extracted from OOXML
 */
interface ParagraphStyle {
  /** Paragraph index (0-based) */
  index: number;
  /** Text alignment: left, center, right, justify */
  alignment?: string;
  /** List level (0-based) if this is a list item */
  listLevel?: number;
  /** Numbering ID reference */
  numId?: string;
  /** Is this an outline-style numbered list (1.1, 1.2, etc.) */
  isOutlineNumbering?: boolean;
  /** Plain text content for matching */
  textContent?: string;
  /** Line spacing multiplier (1 = single, 1.5 = 1.5 lines, 2 = double) */
  lineSpacing?: number;
  /** Space before paragraph in points */
  spaceBefore?: number;
  /** Space after paragraph in points */
  spaceAfter?: number;
  /** First line indent in points (positive = indent, negative = hanging) */
  firstLineIndent?: number;
  /** Left indent in points */
  leftIndent?: number;
  /** Right indent in points */
  rightIndent?: number;
}

/**
 * Numbering definition from OOXML
 */
interface NumberingDefinition {
  numId: string;
  abstractNumId: string;
  levels: {
    level: number;
    format: string; // decimal, lowerLetter, upperLetter, lowerRoman, upperRoman, bullet
    text: string;   // e.g., "%1.", "%1.%2.", etc.
    start: number;
  }[];
}

/**
 * Table cell formatting extracted from OOXML
 */
interface TableCellFormatting {
  /** Row index in the table */
  rowIndex: number;
  /** Cell index in the row */
  cellIndex: number;
  /** Background/shading color */
  backgroundColor?: string;
  /** Border styles */
  borderTop?: { color: string; width: number; style: string };
  borderBottom?: { color: string; width: number; style: string };
  borderLeft?: { color: string; width: number; style: string };
  borderRight?: { color: string; width: number; style: string };
  /** Cell width in points */
  width?: number;
  /** Vertical alignment */
  verticalAlign?: string;
}

/**
 * Table formatting data
 */
interface TableFormatting {
  /** Table index in document */
  tableIndex: number;
  /** Cell formatting by row,cell */
  cells: TableCellFormatting[];
}

/**
 * Run-level formatting extracted from OOXML
 */
interface RunFormatting {
  /** The text content of this run */
  text: string;
  /** Text color in hex format (e.g., "FF0000" for red) */
  color?: string;
  /** Highlight/background color */
  highlight?: string;
  /** Font family from Word (e.g., "Calibri", "Arial") - resolved to supported font or Times New Roman */
  fontFamily?: string;
  /** Font size in points */
  fontSize?: number;
  /** Is subscript */
  subscript?: boolean;
  /** Is superscript */
  superscript?: boolean;
  /** Is bold */
  bold?: boolean;
  /** Is italic */
  italic?: boolean;
  /** Is underline */
  underline?: boolean;
  /** Is strikethrough */
  strike?: boolean;
}

/**
 * Comment extracted from DOCX - compatible with TipTap CommentData
 */
export interface ImportedComment {
  id: string;
  author: string;
  date: string;
  content: string;
  /** The text that this comment is attached to (for reference) */
  commentedText?: string;
  /** TipTap comment ID of the parent comment (if this is a reply) */
  parentCommentId?: string;
  /** Nested reply comments — deprecated, prefer flat list with parentCommentId */
  replies?: ImportedComment[];
}

/**
 * Track change extracted from DOCX - compatible with TipTap TrackChangeInfo
 */
export interface ImportedTrackChange {
  id: string;
  type: "insertion" | "deletion";
  author: string;
  date: string;
  text: string;
  /** Position in accepted text (insertions visible, deletions removed) */
  position: number;
  /** Text immediately before this change (for fallback positioning) */
  contextBefore?: string;
  /** Text immediately after this change (for fallback positioning) */
  contextAfter?: string;
}

/**
 * Internal: Raw comment data from DOCX XML
 */
interface RawComment {
  id: string;
  author: string;
  date: string;
  content: string;
}

// OOXML namespace for Word processing ML
const WORDPROCESSING_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

// ============================================================================
// Track Change Marker System
// ============================================================================
// We use Unicode Private Use Area characters as delimiters for track change
// markers. These characters never appear in regular document text, making
// them safe to inject into the OOXML before mammoth processes it.
// The markers flow through mammoth's conversion as regular text, preserving
// their position relative to surrounding content. After all post-processing
// (paragraph styles, formatting, etc.), we convert them to <span> elements.

/** Marker start delimiter (Unicode Private Use Area) */
const TC_MARKER_START = "\uE000";
/** Marker end delimiter */
const TC_MARKER_END = "\uE001";
/** Separator within marker data */
const TC_MARKER_SEP = "\uE002";
/** Page break marker (Unicode Private Use Area) */
const PB_MARKER = "\uE003";

/** Regex to match any track change or comment marker in text.
 *  Types: I = insertion, D = deletion, C = comment */
const TC_MARKER_REGEX = new RegExp(
  `${TC_MARKER_START}([IDC])(${TC_MARKER_SEP}[^${TC_MARKER_END}]*)?${TC_MARKER_END}`,
  "g"
);

/**
 * Strip all track change and comment markers from text (for text normalization/comparison).
 * Also strips the text content between DEL markers since that text doesn't
 * exist in the original OOXML paragraph text.
 * Comment markers (C) are removed but their enclosed text is kept (same as insertions).
 */
function stripTrackChangeMarkers(text: string): string {
  // First, remove deletion blocks entirely (marker + deleted text + end marker)
  // since this text doesn't exist in the original accepted text
  let result = text.replace(
    new RegExp(
      `${TC_MARKER_START}D${TC_MARKER_SEP}[^${TC_MARKER_END}]*${TC_MARKER_END}[\\s\\S]*?${TC_MARKER_START}/D${TC_MARKER_END}`,
      "g"
    ),
    ""
  );
  // Then remove any remaining markers (insertion/comment start/end — keep enclosed text)
  result = result.replace(TC_MARKER_REGEX, "");
  result = result.replace(
    new RegExp(`${TC_MARKER_START}/[IDC]${TC_MARKER_END}`, "g"),
    ""
  );
  // Remove page break markers
  result = result.replace(new RegExp(PB_MARKER, "g"), "");
  return result;
}

interface TrackChangeMarkerMeta {
  id: string;
  type: "insertion" | "deletion";
  author: string;
  date: string;
}

/**
 * Pre-process the document.xml in a DOCX ZIP to make track changes visible
 * to mammoth. Injects invisible marker text around insertions and converts
 * deletions from ignored elements to visible text with markers.
 * 
 * Returns the modified ZIP and a metadata map for the markers.
 */
async function preprocessDocxForTrackChanges(zip: JSZip): Promise<{
  markerMeta: Map<string, TrackChangeMarkerMeta>;
  hasTrackChanges: boolean;
}> {
  const markerMeta = new Map<string, TrackChangeMarkerMeta>();
  let markerCounter = 0;
  
  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) {
    return { markerMeta, hasTrackChanges: false };
  }
  
  // Check if there are any track changes
  const hasInsertions = documentXml.includes(":ins ") || documentXml.includes("<w:ins ");
  const hasDeletions = documentXml.includes(":del ") || documentXml.includes("<w:del ");
  
  if (!hasInsertions && !hasDeletions) {
    return { markerMeta, hasTrackChanges: false };
  }
  
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, "text/xml");
  
  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    console.error("[DOCX Import] XML parse error during preprocessing:", parseError.textContent);
    return { markerMeta, hasTrackChanges: false };
  }
  
  function generateMarkerId(): string {
    return `tc${++markerCounter}`;
  }
  
  /**
   * Create a text run XML element containing marker text.
   * We create it as a <w:r><w:t> structure that mammoth will read.
   */
  function createMarkerRun(markerText: string): Element {
    const r = doc.createElementNS(WORDPROCESSING_NS, "w:r");
    const t = doc.createElementNS(WORDPROCESSING_NS, "w:t");
    t.setAttribute("xml:space", "preserve");
    t.textContent = markerText;
    r.appendChild(t);
    return r;
  }
  
  /**
   * Build a start marker string: ⟨type⟩⟨sep⟩⟨id⟩
   */
  function startMarker(type: "I" | "D", id: string): string {
    return `${TC_MARKER_START}${type}${TC_MARKER_SEP}${id}${TC_MARKER_END}`;
  }
  
  /**
   * Build an end marker string: ⟨/type⟩
   */
  function endMarker(type: "I" | "D"): string {
    return `${TC_MARKER_START}/${type}${TC_MARKER_END}`;
  }
  
  // Process all <w:ins> elements — add markers around their content
  const insElements = Array.from(doc.getElementsByTagNameNS(WORDPROCESSING_NS, "ins"));
  // Also try without namespace
  const insElementsNoNs = Array.from(doc.getElementsByTagName("w:ins"));
  const allIns = Array.from(new Set([...insElements, ...insElementsNoNs]));
  
  for (const ins of allIns) {
    const author = ins.getAttribute("w:author") || ins.getAttributeNS(WORDPROCESSING_NS, "author") || "Unknown";
    const date = ins.getAttribute("w:date") || ins.getAttributeNS(WORDPROCESSING_NS, "date") || new Date().toISOString();
    
    // Check if this insertion has any text content
    const textEls = ins.getElementsByTagNameNS(WORDPROCESSING_NS, "t");
    const textElsNoNs = ins.getElementsByTagName("w:t");
    let hasText = false;
    for (let i = 0; i < textEls.length; i++) {
      if ((textEls[i].textContent || "").trim()) { hasText = true; break; }
    }
    if (!hasText) {
      for (let i = 0; i < textElsNoNs.length; i++) {
        if ((textElsNoNs[i].textContent || "").trim()) { hasText = true; break; }
      }
    }
    if (!hasText) continue;
    
    const id = generateMarkerId();
    markerMeta.set(id, { id, type: "insertion", author, date });
    
    // Insert start marker before first child
    const startRun = createMarkerRun(startMarker("I", id));
    if (ins.firstChild) {
      ins.insertBefore(startRun, ins.firstChild);
    } else {
      ins.appendChild(startRun);
    }
    
    // Insert end marker after last child
    const endRun = createMarkerRun(endMarker("I"));
    ins.appendChild(endRun);
  }
  
  // Process all <w:del> elements — convert to visible text with markers
  const delElements = Array.from(doc.getElementsByTagNameNS(WORDPROCESSING_NS, "del"));
  const delElementsNoNs = Array.from(doc.getElementsByTagName("w:del"));
  const allDel = Array.from(new Set([...delElements, ...delElementsNoNs]));
  
  for (const del of allDel) {
    const author = del.getAttribute("w:author") || del.getAttributeNS(WORDPROCESSING_NS, "author") || "Unknown";
    const date = del.getAttribute("w:date") || del.getAttributeNS(WORDPROCESSING_NS, "date") || new Date().toISOString();
    
    // Check if this deletion has any text content
    // Word uses <w:delText>, but some generators (like the docx npm package) use <w:t> inside <w:del>
    let hasText = false;
    const checkTags = ["delText", "t"];
    for (const tag of checkTags) {
      if (hasText) break;
      const els = del.getElementsByTagNameNS(WORDPROCESSING_NS, tag);
      for (let i = 0; i < els.length; i++) {
        if ((els[i].textContent || "").trim()) { hasText = true; break; }
      }
      if (!hasText) {
        const elsNoNs = del.getElementsByTagName(`w:${tag}`);
        for (let i = 0; i < elsNoNs.length; i++) {
          if ((elsNoNs[i].textContent || "").trim()) { hasText = true; break; }
        }
      }
    }
    if (!hasText) continue;
    
    const id = generateMarkerId();
    markerMeta.set(id, { id, type: "deletion", author, date });
    
    // Convert all <w:delText> to <w:t> so mammoth will read them
    const delTextNs = Array.from(del.getElementsByTagNameNS(WORDPROCESSING_NS, "delText")) as Element[];
    const delTextNoNs = Array.from(del.getElementsByTagName("w:delText")) as Element[];
    const seenDelText = new Set<Element>();
    const allDelText: Element[] = [];
    for (const el of delTextNs) { if (!seenDelText.has(el)) { seenDelText.add(el); allDelText.push(el); } }
    for (const el of delTextNoNs) { if (!seenDelText.has(el)) { seenDelText.add(el); allDelText.push(el); } }
    for (const dt of allDelText) {
      // Create a <w:t> with the same content
      const newT = doc.createElementNS(
        dt.namespaceURI || WORDPROCESSING_NS,
        dt.prefix ? `${dt.prefix}:t` : "w:t"
      );
      newT.setAttribute("xml:space", "preserve");
      newT.textContent = dt.textContent;
      dt.parentNode?.replaceChild(newT, dt);
    }
    
    // Now we need to move the del's children out of <w:del> (which mammoth ignores)
    // and into the parent. We wrap them in a new structure that mammoth WILL read.
    // Strategy: rename <w:del> to <w:ins> (which mammoth reads as readChildElements)
    // We can't actually rename XML elements, so we'll move children to parent.
    
    const parent = del.parentNode;
    if (!parent) continue;
    
    // Insert start marker before the <w:del>
    const startRun = createMarkerRun(startMarker("D", id));
    parent.insertBefore(startRun, del);
    
    // Move all children out of <w:del> and into parent, before <w:del>
    while (del.firstChild) {
      parent.insertBefore(del.firstChild, del);
    }
    
    // Insert end marker
    const endRunNode = createMarkerRun(endMarker("D"));
    parent.insertBefore(endRunNode, del);
    
    // Remove the now-empty <w:del>
    parent.removeChild(del);
  }
  
  
  // Serialize modified XML back
  const serializer = new XMLSerializer();
  const modifiedXml = serializer.serializeToString(doc);
  
  // Update the ZIP in place with the modified document.xml
  zip.file("word/document.xml", modifiedXml);
  
  return { markerMeta, hasTrackChanges: true };
}

/**
 * Metadata for a comment marker injected into the OOXML.
 */
export interface CommentMarkerMeta {
  /** Marker ID (cm1, cm2, …) */
  id: string;
  /** Original comment ID from word/comments.xml (w:id attribute) */
  commentXmlId: string;
  author: string;
  date: string;
  content: string;
}

/**
 * Pre-process the document.xml in a DOCX ZIP to inject invisible markers
 * around comment ranges.  Mammoth ignores w:commentRangeStart / w:commentRangeEnd,
 * so we inject <w:r><w:t> text runs containing Unicode markers at those positions.
 * The markers travel through mammoth's conversion and end up in the HTML at the
 * correct positions, just like track change markers.
 *
 * Marker format (reuses the same Unicode PUA delimiters as track changes):
 *   Start: \uE000 C \uE002 cm{n} \uE001
 *   End:   \uE000 /C \uE002 cm{n} \uE001   (end marker also carries the ID
 *          so we can match start/end for the same comment when multiple
 *          comments exist)
 *
 * This must be called BEFORE preprocessDocxForTrackChanges because that
 * function modifies document.xml in place and may move nodes around.
 */
async function preprocessDocxForComments(
  zip: JSZip,
  rawComments: RawComment[]
): Promise<{
  commentMarkerMeta: Map<string, CommentMarkerMeta>;
  hasComments: boolean;
}> {
  const commentMarkerMeta = new Map<string, CommentMarkerMeta>();
  let markerCounter = 0;

  if (rawComments.length === 0) {
    return { commentMarkerMeta, hasComments: false };
  }

  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) {
    return { commentMarkerMeta, hasComments: false };
  }

  // Quick check — does the document have any comment-related elements?
  const hasAnyCommentElement =
    documentXml.includes("commentRangeStart") ||
    documentXml.includes("commentReference");
  if (!hasAnyCommentElement) {
    return { commentMarkerMeta, hasComments: false };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, "text/xml");

  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    console.error("[DOCX Import] XML parse error during comment preprocessing:", parseError.textContent);
    return { commentMarkerMeta, hasComments: false };
  }

  // Build a lookup from XML comment ID → RawComment
  const commentMap = new Map(rawComments.map(c => [c.id, c]));

  function generateMarkerId(): string {
    return `cm${++markerCounter}`;
  }

  function createMarkerRun(markerText: string): Element {
    const r = doc.createElementNS(WORDPROCESSING_NS, "w:r");
    const t = doc.createElementNS(WORDPROCESSING_NS, "w:t");
    t.setAttribute("xml:space", "preserve");
    t.textContent = markerText;
    r.appendChild(t);
    return r;
  }

  /** Get an attribute value trying multiple namespace approaches */
  function getCommentAttr(el: Element, localName: string): string {
    return el.getAttribute(`w:${localName}`)
      || el.getAttribute(localName)
      || el.getAttributeNS(WORDPROCESSING_NS, localName)
      || "";
  }

  function startMarkerStr(id: string): string {
    return `${TC_MARKER_START}C${TC_MARKER_SEP}${id}${TC_MARKER_END}`;
  }

  function endMarkerStr(id: string): string {
    return `${TC_MARKER_START}/C${TC_MARKER_SEP}${id}${TC_MARKER_END}`;
  }

  /** Collect and deduplicate elements found via both getElementsByTagName
   *  and getElementsByTagNameNS (covers all namespace variations). */
  function collectElements(localName: string): Element[] {
    const byNS = Array.from(doc.getElementsByTagNameNS(WORDPROCESSING_NS, localName));
    const byTag = Array.from(doc.getElementsByTagName(`w:${localName}`));
    const seen = new Set<Element>();
    const result: Element[] = [];
    for (const el of [...byNS, ...byTag]) {
      if (!seen.has(el)) { seen.add(el); result.push(el); }
    }
    return result;
  }

  /**
   * Find the first <w:t> text element in the next sibling <w:r> run(s)
   * after the given element. Skips over non-run siblings like other
   * commentRangeStart elements.
   */
  function findFirstTextNodeAfter(el: Node): Element | null {
    let sibling = el.nextSibling;
    while (sibling) {
      if (sibling.nodeType === 1) {
        const elem = sibling as Element;
        const localName = elem.localName || "";
        // Found a <w:r> run — look for <w:t> inside it
        if (localName === "r") {
          const tElements = elem.getElementsByTagNameNS(WORDPROCESSING_NS, "t");
          if (tElements.length > 0) return tElements[0];
          const tByTag = elem.getElementsByTagName("w:t");
          if (tByTag.length > 0) return tByTag[0];
        }
        // Skip over non-content elements (other commentRangeStart, bookmarkStart, etc.)
        // but stop if we hit a paragraph boundary or another block element
        if (localName === "p" || localName === "tbl") break;
      }
      sibling = sibling.nextSibling;
    }
    return null;
  }

  /**
   * Find the last <w:t> text element in the previous sibling <w:r> run(s)
   * before the given element. Skips over non-run siblings like
   * commentReference runs.
   */
  function findLastTextNodeBefore(el: Node): Element | null {
    let sibling = el.previousSibling;
    while (sibling) {
      if (sibling.nodeType === 1) {
        const elem = sibling as Element;
        const localName = elem.localName || "";
        if (localName === "r") {
          // Check if this run has <w:t> elements (skip commentReference-only runs)
          const tElements = elem.getElementsByTagNameNS(WORDPROCESSING_NS, "t");
          if (tElements.length > 0) return tElements[tElements.length - 1];
          const tByTag = elem.getElementsByTagName("w:t");
          if (tByTag.length > 0) return tByTag[tByTag.length - 1];
        }
        if (localName === "p" || localName === "tbl") break;
      }
      sibling = sibling.previousSibling;
    }
    return null;
  }

  // --- Collect range start/end elements ---
  const uniqueStarts = collectElements("commentRangeStart");
  const allEnds = collectElements("commentRangeEnd");
  const endsByCommentId = new Map<string, Element>();
  for (const el of allEnds) {
    const commentId = getCommentAttr(el, "id");
    if (commentId) endsByCommentId.set(commentId, el);
  }

  // --- Collect commentReference elements (for point-comment fallback) ---
  const allRefs = collectElements("commentReference");
  const refsByCommentId = new Map<string, Element>();
  for (const el of allRefs) {
    const commentId = getCommentAttr(el, "id");
    if (commentId) refsByCommentId.set(commentId, el);
  }

  // Track which comments we've handled
  const handledCommentIds = new Set<string>();
  let injectedCount = 0;

  // --- Pass 1: Ranged comments (commentRangeStart + commentRangeEnd) ---
  for (const rangeStart of uniqueStarts) {
    const commentXmlId = getCommentAttr(rangeStart, "id");
    if (!commentXmlId) continue;

    const rawComment = commentMap.get(commentXmlId);
    if (!rawComment) {
      continue;
    }

    const rangeEnd = endsByCommentId.get(commentXmlId);
    if (!rangeEnd) {
      continue;
    }

    if (!rangeStart.parentNode || !rangeEnd.parentNode) {
      continue;
    }

    const markerId = generateMarkerId();
    commentMarkerMeta.set(markerId, {
      id: markerId,
      commentXmlId,
      author: rawComment.author,
      date: rawComment.date,
      content: rawComment.content,
    });

    // Inject start marker INTO the first <w:t> after commentRangeStart.
    // This ensures the marker shares the same formatting span as the
    // commented text after mammoth conversion, so DOM round-trips
    // (e.g. uploadDocxImagesToGCS) can't separate them into different spans.
    const startTextNode = findFirstTextNodeAfter(rangeStart);
    if (startTextNode) {
      startTextNode.textContent = startMarkerStr(markerId) + (startTextNode.textContent || "");
    } else {
      // Fallback: create a separate run (may get separated by DOM round-trips)
      const startRun = createMarkerRun(startMarkerStr(markerId));
      rangeStart.parentNode.insertBefore(startRun, rangeStart.nextSibling);
    }

    // Inject end marker INTO the last <w:t> before commentRangeEnd.
    const endTextNode = findLastTextNodeBefore(rangeEnd);
    if (endTextNode) {
      endTextNode.textContent = (endTextNode.textContent || "") + endMarkerStr(markerId);
    } else {
      // Fallback: create a separate run
      const endRun = createMarkerRun(endMarkerStr(markerId));
      rangeEnd.parentNode.insertBefore(endRun, rangeEnd);
    }

    handledCommentIds.add(commentXmlId);
    injectedCount++;
  }

  // --- Pass 2: Point comments (commentReference only, no range markers) ---
  // These comments are anchored at a specific position but don't span text.
  // We place start+end markers adjacent to the commentReference's parent <w:r>,
  // wrapping the preceding text run (sibling) if one exists, so TipTap can
  // highlight which text the comment is near. If no preceding text run exists,
  // we wrap the next sibling text run instead. As a last resort we place a
  // zero-width pair right before the commentReference run.
  for (const entry of Array.from(commentMap.entries())) {
    const commentXmlId = entry[0];
    const rawComment = entry[1];
    if (handledCommentIds.has(commentXmlId)) continue;

    const ref = refsByCommentId.get(commentXmlId);
    if (!ref) {
      continue;
    }

    // The commentReference is inside a <w:r>. Find that run.
    const refRun = findAncestorByLocalName(ref, "r");
    if (!refRun || !refRun.parentNode) {
      continue;
    }

    // Look for the nearest preceding sibling <w:r> that has text
    let targetBefore: Element | null = null;
    let targetAfter: Element | null = null;
    let sibling = refRun.previousSibling;
    while (sibling) {
      if (sibling.nodeType === 1 && isTextRun(sibling as Element)) {
        targetBefore = sibling as Element;
        break;
      }
      sibling = sibling.previousSibling;
    }
    if (!targetBefore) {
      sibling = refRun.nextSibling;
      while (sibling) {
        if (sibling.nodeType === 1 && isTextRun(sibling as Element)) {
          targetAfter = sibling as Element;
          break;
        }
        sibling = sibling.nextSibling;
      }
    }

    const markerId = generateMarkerId();
    commentMarkerMeta.set(markerId, {
      id: markerId,
      commentXmlId,
      author: rawComment.author,
      date: rawComment.date,
      content: rawComment.content,
    });

    const startRun = createMarkerRun(startMarkerStr(markerId));
    const endRun = createMarkerRun(endMarkerStr(markerId));

    if (targetBefore) {
      // Wrap the preceding text run
      targetBefore.parentNode!.insertBefore(startRun, targetBefore);
      targetBefore.parentNode!.insertBefore(endRun, targetBefore.nextSibling);
    } else if (targetAfter) {
      // Wrap the following text run
      targetAfter.parentNode!.insertBefore(startRun, targetAfter);
      targetAfter.parentNode!.insertBefore(endRun, targetAfter.nextSibling);
    } else {
      // No adjacent text — place zero-width markers before the reference run
      refRun.parentNode!.insertBefore(startRun, refRun);
      refRun.parentNode!.insertBefore(endRun, refRun);
    }

    handledCommentIds.add(commentXmlId);
    injectedCount++;
  }

  if (injectedCount === 0) {
    return { commentMarkerMeta, hasComments: false };
  }

  // Serialize modified XML back
  const serializer = new XMLSerializer();
  const modifiedXml = serializer.serializeToString(doc);
  zip.file("word/document.xml", modifiedXml);

  return { commentMarkerMeta, hasComments: true };
}

/** Walk up the DOM to find an ancestor element with the given local name */
function findAncestorByLocalName(node: Node, localName: string): Element | null {
  let current = node.parentNode;
  while (current) {
    if (current.nodeType === 1) {
      const el = current as Element;
      if (el.localName === localName || el.nodeName === `w:${localName}`) {
        return el;
      }
    }
    current = current.parentNode;
  }
  return null;
}

/** Check if a <w:r> element contains visible text (has <w:t> children) */
function isTextRun(el: Element): boolean {
  const localName = el.localName || el.nodeName.replace(/^w:/, "");
  if (localName !== "r") return false;
  // Check for w:t children
  const tElements = el.getElementsByTagNameNS(WORDPROCESSING_NS, "t");
  if (tElements.length > 0) return true;
  const tByTag = el.getElementsByTagName("w:t");
  return tByTag.length > 0;
}

/**
 * Convert track change markers in HTML to proper <span> elements.
 * This is the final step after all formatting post-processing is done.
 * 
 * Walks DOM text nodes to find markers (safe from matching in HTML attributes),
 * then replaps them with track change spans.
 */
function convertMarkersToTrackChangeSpans(
  html: string,
  markerMeta: Map<string, TrackChangeMarkerMeta>
): { html: string; trackChanges: ImportedTrackChange[] } {
  const trackChanges: ImportedTrackChange[] = [];
  
  // Replace markers in the HTML string directly. This is safe because markers
  // are ONLY in text content (they were injected as <w:t> text in OOXML,
  // so mammoth outputs them as plain text in HTML — never inside attributes).
  let processedHtml = html;
  
  // Replace marker blocks with spans
  // Pattern: ⟨type⟩⟨sep⟩⟨id⟩ ... content ... ⟨/type⟩
  processedHtml = processedHtml.replace(
    new RegExp(
      // Start marker: E000 + type(I|D) + E002 + id + E001
      `${escapeRegExpForMarkers(TC_MARKER_START)}([ID])${escapeRegExpForMarkers(TC_MARKER_SEP)}([a-z0-9]+)${escapeRegExpForMarkers(TC_MARKER_END)}` +
      // Content (may contain HTML tags)
      `([\\s\\S]*?)` +
      // End marker: E000 + /type + E001
      `${escapeRegExpForMarkers(TC_MARKER_START)}/(\\1)${escapeRegExpForMarkers(TC_MARKER_END)}`,
      "g"
    ),
    (_match, typeChar, id, content, _endType) => {
      const meta = markerMeta.get(id);
      if (!meta) {
        return content; // Just return content without wrapping
      }
      
      const changeType = typeChar === "I" ? "insertion" : "deletion";
      const tiptapChangeId = generateTrackChangeId();
      const cssClass = `track-change-${changeType}`;
      
      trackChanges.push({
        id: tiptapChangeId,
        type: changeType,
        author: meta.author,
        date: meta.date,
        text: content.replace(/<[^>]*>/g, ""), // Strip HTML tags for text
        position: 0, // Not needed — marks are positionally correct in HTML
        contextBefore: "",
        contextAfter: "",
      });
      
      return `<span data-change-type="${changeType}" data-change-id="${tiptapChangeId}" data-change-author="${escapeHtmlAttr(meta.author)}" data-change-date="${escapeHtmlAttr(meta.date)}" class="${cssClass}">${content}</span>`;
    }
  );
  
  // Clean up any stray track-change markers that didn't match the block pattern.
  // Only target I/D markers — leave C (comment) markers for the comment conversion step.
  processedHtml = processedHtml.replace(
    new RegExp(`${escapeRegExpForMarkers(TC_MARKER_START)}/?[ID]${escapeRegExpForMarkers(TC_MARKER_SEP)}?[^${escapeRegExpForMarkers(TC_MARKER_END)}]*${escapeRegExpForMarkers(TC_MARKER_END)}`, "g"),
    ""
  );

  return { html: processedHtml, trackChanges };
}

/**
 * Convert comment markers in HTML to proper <span data-comment-id> elements.
 * Same principle as convertMarkersToTrackChangeSpans but for comment ranges.
 *
 * Marker format:
 *   Start: \uE000 C \uE002 cm{n} \uE001
 *   End:   \uE000 /C \uE002 cm{n} \uE001
 */
export function convertCommentMarkersToSpans(
  html: string,
  commentMarkerMeta: Map<string, CommentMarkerMeta>
): { html: string; comments: ImportedComment[] } {
  const comments: ImportedComment[] = [];

  // Use a position-based approach instead of a single regex replacement.
  // Comment ranges in DOCX can OVERLAP (e.g. a reply range that partially
  // overlaps the original comment). A single regex with backreferences
  // consumes inner markers when replacing outer ones, losing comments.
  //
  // Strategy: find every individual start/end marker, sort by position,
  // then scan left-to-right replacing each marker with the appropriate
  // <span> open or </span> close tag. Overlapping spans are valid for
  // TipTap since comments are marks (which natively support overlap).

  const startRegex = new RegExp(
    `${escapeRegExpForMarkers(TC_MARKER_START)}C${escapeRegExpForMarkers(TC_MARKER_SEP)}([a-z0-9]+)${escapeRegExpForMarkers(TC_MARKER_END)}`,
    "g"
  );
  const endRegex = new RegExp(
    `${escapeRegExpForMarkers(TC_MARKER_START)}/C${escapeRegExpForMarkers(TC_MARKER_SEP)}([a-z0-9]+)${escapeRegExpForMarkers(TC_MARKER_END)}`,
    "g"
  );

  interface MarkerPos {
    type: "start" | "end";
    markerId: string;
    pos: number;
    length: number;
  }

  const markers: MarkerPos[] = [];
  let m: RegExpExecArray | null;

  while ((m = startRegex.exec(html)) !== null) {
    markers.push({ type: "start", markerId: m[1], pos: m.index, length: m[0].length });
  }
  while ((m = endRegex.exec(html)) !== null) {
    markers.push({ type: "end", markerId: m[1], pos: m.index, length: m[0].length });
  }

  if (markers.length === 0) {
    return { html, comments };
  }

  // Sort by position (stable — start before end when at the same position)
  markers.sort((a, b) => a.pos - b.pos || (a.type === "start" ? -1 : 1));

  // Map from markerId → tiptapCommentId + start position (for commentedText)
  const activeComments = new Map<string, { tiptapId: string; textStart: number }>();
  // Map markerId → meta for building comments list
  const markerIdToTiptapId = new Map<string, string>();

  // Build result by replacing markers with span tags
  let result = "";
  let lastPos = 0;

  for (const marker of markers) {
    // Copy text before this marker
    result += html.substring(lastPos, marker.pos);

    if (marker.type === "start") {
      const meta = commentMarkerMeta.get(marker.markerId);
      if (meta) {
        const tiptapCommentId = generateCommentId();
        markerIdToTiptapId.set(marker.markerId, tiptapCommentId);
        // Record text position in the *result* string so we can extract
        // the annotated text later.
        activeComments.set(marker.markerId, {
          tiptapId: tiptapCommentId,
          textStart: result.length,
        });
        result += `<span data-comment-id="${escapeHtmlAttr(tiptapCommentId)}" class="tiptap-comment">`;
      }
    } else {
      const active = activeComments.get(marker.markerId);
      const meta = commentMarkerMeta.get(marker.markerId);
      if (active && meta) {
        result += "</span>";
        // Extract the annotated text (strip HTML tags)
        const annotatedHtml = result.substring(active.textStart);
        const commentedText = annotatedHtml
          .replace(/<[^>]*>/g, "")
          .trim();

        comments.push({
          id: active.tiptapId,
          author: meta.author,
          date: meta.date,
          content: meta.content,
          commentedText,
        });
        activeComments.delete(marker.markerId);
      }
    }

    lastPos = marker.pos + marker.length;
  }

  // Append remaining HTML after the last marker
  result += html.substring(lastPos);

  // Close any unclosed comment spans (orphaned start markers)
  activeComments.forEach((active, markerId) => {
    const meta = commentMarkerMeta.get(markerId);
    if (meta) {
      result += "</span>";
      comments.push({
        id: active.tiptapId,
        author: meta.author,
        date: meta.date,
        content: meta.content,
      });
    }
  });

  // Clean up any stray comment markers that weren't matched
  result = result.replace(
    new RegExp(
      `${escapeRegExpForMarkers(TC_MARKER_START)}/?C${escapeRegExpForMarkers(TC_MARKER_SEP)}?[^${escapeRegExpForMarkers(TC_MARKER_END)}]*${escapeRegExpForMarkers(TC_MARKER_END)}`,
      "g"
    ),
    ""
  );

  return { html: result, comments };
}

/** Escape special regex characters in marker delimiter strings */
function escapeRegExpForMarkers(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Escape special regex characters in a string */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Get attribute value with namespace handling
 */
function getAttr(element: Element, localName: string): string | null {
  // Try with w: prefix first
  let value = element.getAttribute(`w:${localName}`);
  if (value) return value;
  // Try without prefix
  value = element.getAttribute(localName);
  if (value) return value;
  // Try with namespace
  value = element.getAttributeNS(WORDPROCESSING_NS, localName);
  return value;
}

/**
 * Extract numbering definitions from word/numbering.xml
 */
async function extractNumberingDefinitions(zip: JSZip): Promise<Map<string, NumberingDefinition>> {
  const definitions = new Map<string, NumberingDefinition>();
  
  const numberingXml = await zip.file("word/numbering.xml")?.async("string");
  if (!numberingXml) return definitions;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(numberingXml, "text/xml");
  
  // First, parse abstract numbering definitions (templates)
  const abstractNumMap = new Map<string, { levels: NumberingDefinition["levels"] }>();
  
  const abstractNums = doc.getElementsByTagName("w:abstractNum");
  for (let i = 0; i < abstractNums.length; i++) {
    const abstractNum = abstractNums[i];
    const abstractNumId = getAttr(abstractNum, "abstractNumId") || "";
    
    const levels: NumberingDefinition["levels"] = [];
    const lvlElements = abstractNum.getElementsByTagName("w:lvl");
    
    for (let j = 0; j < lvlElements.length; j++) {
      const lvl = lvlElements[j];
      const ilvl = parseInt(getAttr(lvl, "ilvl") || "0", 10);
      
      // Get numbering format
      const numFmtEl = lvl.getElementsByTagName("w:numFmt")[0];
      const format = numFmtEl ? (getAttr(numFmtEl, "val") || "decimal") : "decimal";
      
      // Get level text (e.g., "%1.", "%1.%2.")
      const lvlTextEl = lvl.getElementsByTagName("w:lvlText")[0];
      const text = lvlTextEl ? (getAttr(lvlTextEl, "val") || "%1.") : "%1.";
      
      // Get start number
      const startEl = lvl.getElementsByTagName("w:start")[0];
      const start = startEl ? parseInt(getAttr(startEl, "val") || "1", 10) : 1;
      
      levels.push({ level: ilvl, format, text, start });
    }
    
    // Sort levels by level number
    levels.sort((a, b) => a.level - b.level);
    abstractNumMap.set(abstractNumId, { levels });
  }
  
  // Now parse num elements (instances of abstract numbering)
  const nums = doc.getElementsByTagName("w:num");
  for (let i = 0; i < nums.length; i++) {
    const num = nums[i];
    const numId = getAttr(num, "numId") || "";
    
    const abstractNumIdEl = num.getElementsByTagName("w:abstractNumId")[0];
    const abstractNumId = abstractNumIdEl ? (getAttr(abstractNumIdEl, "val") || "") : "";
    
    const abstractDef = abstractNumMap.get(abstractNumId);
    if (abstractDef) {
      definitions.set(numId, {
        numId,
        abstractNumId,
        levels: abstractDef.levels,
      });
    }
  }
  
  return definitions;
}

/**
 * Extract paragraph styles from document.xml
 * Returns information about alignment, numbering, etc. for each paragraph
 */
async function extractParagraphStyles(zip: JSZip): Promise<ParagraphStyle[]> {
  const styles: ParagraphStyle[] = [];
  
  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) return styles;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, "text/xml");
  
  // Find all paragraph elements
  const paragraphs = doc.getElementsByTagName("w:p");
  
  for (let i = 0; i < paragraphs.length; i++) {
    const para = paragraphs[i];
    const style: ParagraphStyle = { index: i };
    
    // Get paragraph properties
    const pPr = para.getElementsByTagName("w:pPr")[0];
    if (pPr) {
      // Get text alignment (jc = justification)
      const jc = pPr.getElementsByTagName("w:jc")[0];
      if (jc) {
        const val = getAttr(jc, "val");
        if (val) {
          // Map Word alignment values to CSS
          const alignmentMap: Record<string, string> = {
            "left": "left",
            "center": "center",
            "right": "right",
            "both": "justify",
            "justify": "justify",
          };
          style.alignment = alignmentMap[val] || val;
        }
      }
      
      // Get numbering properties
      const numPr = pPr.getElementsByTagName("w:numPr")[0];
      if (numPr) {
        const ilvlEl = numPr.getElementsByTagName("w:ilvl")[0];
        const numIdEl = numPr.getElementsByTagName("w:numId")[0];
        
        if (ilvlEl) {
          style.listLevel = parseInt(getAttr(ilvlEl, "val") || "0", 10);
        }
        if (numIdEl) {
          style.numId = getAttr(numIdEl, "val") || undefined;
        }
      }
      
      // Get spacing properties (w:spacing)
      const spacing = pPr.getElementsByTagName("w:spacing")[0];
      if (spacing) {
        // Line spacing (w:line) - value is in 240ths of a line (240 = single)
        // w:lineRule can be "auto" (multiple), "exact", or "atLeast"
        const lineVal = getAttr(spacing, "line");
        const lineRule = getAttr(spacing, "lineRule");
        if (lineVal) {
          const lineValue = parseInt(lineVal, 10);
          if (!isNaN(lineValue)) {
            if (lineRule === "auto" || !lineRule) {
              // "auto" means the value is a multiple of single line spacing (240 = 1.0)
              style.lineSpacing = lineValue / 240;
            } else if (lineRule === "exact" || lineRule === "atLeast") {
              // Value is in twips (1/20 of a point)
              // Convert to approximate line height multiplier assuming 12pt base
              style.lineSpacing = lineValue / 240;
            }
          }
        }
        
        // Space before (w:before) - value is in twips (1/20 of a point)
        const beforeVal = getAttr(spacing, "before");
        if (beforeVal) {
          const twips = parseInt(beforeVal, 10);
          if (!isNaN(twips)) {
            style.spaceBefore = twips / 20; // Convert to points
          }
        }
        
        // Space after (w:after) - value is in twips
        const afterVal = getAttr(spacing, "after");
        if (afterVal) {
          const twips = parseInt(afterVal, 10);
          if (!isNaN(twips)) {
            style.spaceAfter = twips / 20; // Convert to points
          }
        }
      }
      
      // Get indentation properties (w:ind)
      const ind = pPr.getElementsByTagName("w:ind")[0];
      if (ind) {
        // First line indent (w:firstLine) - positive indent
        const firstLineVal = getAttr(ind, "firstLine");
        if (firstLineVal) {
          const twips = parseInt(firstLineVal, 10);
          if (!isNaN(twips)) {
            style.firstLineIndent = twips / 20; // Convert to points
          }
        }
        
        // Hanging indent (w:hanging) - first line goes left, rest indented
        const hangingVal = getAttr(ind, "hanging");
        if (hangingVal && !style.firstLineIndent) {
          const twips = parseInt(hangingVal, 10);
          if (!isNaN(twips)) {
            style.firstLineIndent = -(twips / 20); // Negative for hanging
          }
        }
        
        // Left indent (w:left)
        const leftVal = getAttr(ind, "left");
        if (leftVal) {
          const twips = parseInt(leftVal, 10);
          if (!isNaN(twips)) {
            style.leftIndent = twips / 20; // Convert to points
          }
        }
        
        // Right indent (w:right)
        const rightVal = getAttr(ind, "right");
        if (rightVal) {
          const twips = parseInt(rightVal, 10);
          if (!isNaN(twips)) {
            style.rightIndent = twips / 20; // Convert to points
          }
        }
      }
    }
    
    // Extract text content for matching
    let textContent = "";
    const textElements = para.getElementsByTagName("w:t");
    for (let j = 0; j < textElements.length; j++) {
      textContent += textElements[j].textContent || "";
    }
    style.textContent = textContent;
    
    styles.push(style);
  }
  
  return styles;
}

/**
 * Check if a numbering definition uses outline numbering (1.1, 1.2, etc.)
 */
function isOutlineNumbering(def: NumberingDefinition): boolean {
  // Check if level text patterns indicate outline numbering
  // Outline numbering typically has patterns like "%1.", "%1.%2.", "%1.%2.%3."
  if (def.levels.length < 2) return false;
  
  // Check if any level beyond 0 includes reference to parent levels
  // Level 1 should have %1.%2, Level 2 should have %1.%2.%3, etc.
  for (let i = 1; i < def.levels.length; i++) {
    const levelText = def.levels[i].text;
    // Check if this level includes parent level references (%1 at level 1, %1 and %2 at level 2, etc.)
    // A true outline numbering has all parent placeholders
    let hasAllParentRefs = true;
    for (let j = 1; j <= i; j++) {
      if (!levelText.includes(`%${j}`)) {
        hasAllParentRefs = false;
        break;
      }
    }
    if (hasAllParentRefs && levelText.includes(`%${i + 1}`)) {
      return true;
    }
  }
  
  // Also check if level 0 is just "%1" and level 1 is "%1.%2" pattern
  if (def.levels.length >= 2) {
    const level0 = def.levels[0]?.text || "";
    const level1 = def.levels[1]?.text || "";
    if (level0.includes("%1") && level1.includes("%1") && level1.includes("%2")) {
      return true;
    }
  }
  
  return false;
}

/**
 * Map Word highlight color names to CSS colors
 */
const HIGHLIGHT_COLOR_MAP: Record<string, string> = {
  "yellow": "#FFFF00",
  "green": "#00FF00",
  "cyan": "#00FFFF",
  "magenta": "#FF00FF",
  "blue": "#0000FF",
  "red": "#FF0000",
  "darkBlue": "#000080",
  "darkCyan": "#008080",
  "darkGreen": "#008000",
  "darkMagenta": "#800080",
  "darkRed": "#800000",
  "darkYellow": "#808000",
  "darkGray": "#808080",
  "lightGray": "#C0C0C0",
  "black": "#000000",
  "white": "#FFFFFF",
};

// Font normalization moved to ./fontMap.ts so the clipboard-paste path
// (TipTapEditor.tsx transformPastedHTML) can share the same canonical
// mapping. Local alias keeps existing call sites readable.
const resolveFontFamily = resolveFontFamilyOrDefault;

/**
 * Extract document default font from word/styles.xml (docDefaults).
 * Used when runs inherit font from the document style rather than having explicit w:rFonts.
 */
async function extractDocumentDefaultFont(zip: JSZip): Promise<string | null> {
  const stylesXml = await zip.file("word/styles.xml")?.async("string");
  if (!stylesXml) return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(stylesXml, "text/xml");
  const docDefaults = doc.getElementsByTagName("w:docDefaults")[0];
  if (!docDefaults) return null;
  const rPrDefault = docDefaults.getElementsByTagName("w:rPrDefault")[0];
  if (!rPrDefault) return null;
  const rPr = rPrDefault.getElementsByTagName("w:rPr")[0];
  if (!rPr) return null;
  const rFontsEl = rPr.getElementsByTagName("w:rFonts")[0];
  if (!rFontsEl) return null;
  const fontName = getAttr(rFontsEl, "ascii") || getAttr(rFontsEl, "hAnsi") || getAttr(rFontsEl, "cs");
  return fontName || null;
}

/**
 * Extract run-level formatting from document.xml
 * Returns a list of text runs with their formatting
 */
async function extractRunFormatting(zip: JSZip): Promise<RunFormatting[]> {
  const runs: RunFormatting[] = [];
  
  const [documentXml, defaultFont] = await Promise.all([
    zip.file("word/document.xml")?.async("string"),
    extractDocumentDefaultFont(zip),
  ]);
  
  if (!documentXml) return runs;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, "text/xml");
  
  // Find all run elements (w:r)
  const runElements = doc.getElementsByTagName("w:r");
  
  for (let i = 0; i < runElements.length; i++) {
    const run = runElements[i];
    
    // Get text content from w:t elements
    let text = "";
    const textElements = run.getElementsByTagName("w:t");
    for (let j = 0; j < textElements.length; j++) {
      text += textElements[j].textContent || "";
    }
    
    // Skip empty runs
    if (!text) continue;
    
    const formatting: RunFormatting = { text };
    
    // Get run properties (w:rPr)
    const rPr = run.getElementsByTagName("w:rPr")[0];
    if (rPr) {
      // Text color (w:color)
      const colorEl = rPr.getElementsByTagName("w:color")[0];
      if (colorEl) {
        const colorVal = getAttr(colorEl, "val");
        if (colorVal && colorVal !== "auto" && colorVal !== "000000") {
          formatting.color = colorVal;
        }
      }
      
      // Highlight color (w:highlight)
      const highlightEl = rPr.getElementsByTagName("w:highlight")[0];
      if (highlightEl) {
        const highlightVal = getAttr(highlightEl, "val");
        if (highlightVal && highlightVal !== "none") {
          formatting.highlight = HIGHLIGHT_COLOR_MAP[highlightVal] || highlightVal;
        }
      }
      
      // Shading/background color (w:shd) - alternative to highlight
      if (!formatting.highlight) {
        const shdEl = rPr.getElementsByTagName("w:shd")[0];
        if (shdEl) {
          const fillVal = getAttr(shdEl, "fill");
          if (fillVal && fillVal !== "auto" && fillVal !== "FFFFFF") {
            formatting.highlight = `#${fillVal}`;
          }
        }
      }
      
      // Font family (w:rFonts) - w:ascii or w:hAnsi; fallback to document default
      const rFontsEl = rPr.getElementsByTagName("w:rFonts")[0];
      if (rFontsEl) {
        const fontName = getAttr(rFontsEl, "ascii") || getAttr(rFontsEl, "hAnsi") || getAttr(rFontsEl, "cs");
        if (fontName) {
          formatting.fontFamily = fontName;
        }
      }

      // Font size (w:sz) - value is in half-points
      const szEl = rPr.getElementsByTagName("w:sz")[0];
      if (szEl) {
        const szVal = getAttr(szEl, "val");
        if (szVal) {
          const halfPoints = parseInt(szVal, 10);
          if (!isNaN(halfPoints)) {
            formatting.fontSize = halfPoints / 2; // Convert to points
          }
        }
      }
      
      // Subscript (w:vertAlign val="subscript")
      const vertAlignEl = rPr.getElementsByTagName("w:vertAlign")[0];
      if (vertAlignEl) {
        const vertAlignVal = getAttr(vertAlignEl, "val");
        if (vertAlignVal === "subscript") {
          formatting.subscript = true;
        } else if (vertAlignVal === "superscript") {
          formatting.superscript = true;
        }
      }
      
      // Bold (w:b)
      const boldEl = rPr.getElementsByTagName("w:b")[0];
      if (boldEl) {
        const boldVal = getAttr(boldEl, "val");
        formatting.bold = boldVal !== "0" && boldVal !== "false";
      }
      
      // Italic (w:i)
      const italicEl = rPr.getElementsByTagName("w:i")[0];
      if (italicEl) {
        const italicVal = getAttr(italicEl, "val");
        formatting.italic = italicVal !== "0" && italicVal !== "false";
      }
      
      // Underline (w:u)
      const underlineEl = rPr.getElementsByTagName("w:u")[0];
      if (underlineEl) {
        const underlineVal = getAttr(underlineEl, "val");
        formatting.underline = underlineVal !== "none" && !!underlineVal;
      }
      
      // Strikethrough (w:strike)
      const strikeEl = rPr.getElementsByTagName("w:strike")[0];
      if (strikeEl) {
        const strikeVal = getAttr(strikeEl, "val");
        formatting.strike = strikeVal !== "0" && strikeVal !== "false";
      }
    }
    // If no explicit font, use document default (e.g. from styles.xml docDefaults)
    if (!formatting.fontFamily && defaultFont) {
      formatting.fontFamily = defaultFont;
    }
    // Don't apply font when it resolves to Times New Roman (document default) - avoids redundant spans
    if (formatting.fontFamily && resolveFontFamily(formatting.fontFamily) === DEFAULT_FONT) {
      delete formatting.fontFamily;
    }
    
    // Only add if there's some formatting beyond just bold/italic (mammoth handles those)
    if (formatting.color || formatting.highlight || formatting.fontFamily || formatting.fontSize ||
        formatting.subscript || formatting.superscript) {
      runs.push(formatting);
    }
  }
  
  return runs;
}

/**
 * Pre-process the document.xml in a DOCX ZIP to replace page breaks with
 * Unicode markers. The markers flow through mammoth's conversion as regular
 * text, preserving their position. After conversion, we replace them with
 * actual page break HTML elements.
 *
 * Handles:
 * - <w:br w:type="page"/> elements (explicit page breaks)
 * - <w:sectPr> with type="nextPage" (section-based page breaks)
 */
async function preprocessDocxForPageBreaks(zip: JSZip): Promise<{
  hasPageBreaks: boolean;
}> {
  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) return { hasPageBreaks: false };

  // Quick check if there are any page breaks
  const hasBreakElement = documentXml.includes("w:type=\"page\"") ||
                          documentXml.includes("w:type='page'") ||
                          documentXml.includes("w:val=\"nextPage\"") ||
                          documentXml.includes("w:val='nextPage'") ||
                          documentXml.includes("w:val=\"oddPage\"") ||
                          documentXml.includes("w:val=\"evenPage\"");
  if (!hasBreakElement) return { hasPageBreaks: false };

  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, "text/xml");

  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    console.error("[DOCX Import] XML parse error during page break preprocessing:", parseError.textContent);
    return { hasPageBreaks: false };
  }

  let foundPageBreaks = 0;

  function createMarkerRun(): Element {
    const r = doc.createElementNS(WORDPROCESSING_NS, "w:r");
    const t = doc.createElementNS(WORDPROCESSING_NS, "w:t");
    t.setAttribute("xml:space", "preserve");
    t.textContent = PB_MARKER;
    r.appendChild(t);
    return r;
  }

  // 1. Find <w:br w:type="page"/> elements and replace with marker text
  const brElements = Array.from(doc.getElementsByTagNameNS(WORDPROCESSING_NS, "br"));
  const brElementsNoNs = Array.from(doc.getElementsByTagName("w:br"));
  const allBreaks = Array.from(new Set([...brElements, ...brElementsNoNs]));

  for (const br of allBreaks) {
    const brType = br.getAttribute("w:type") ||
                   br.getAttributeNS(WORDPROCESSING_NS, "type") ||
                   getAttr(br, "type");
    if (brType === "page") {
      // Replace the <w:br> with a marker text run in the parent <w:r>
      const parentRun = br.parentNode;
      if (parentRun) {
        const markerRun = createMarkerRun();
        // Insert marker run after the current run (in the paragraph)
        const parentParagraph = parentRun.parentNode;
        if (parentParagraph) {
          parentParagraph.insertBefore(markerRun, parentRun.nextSibling);
        }
        // Remove the <w:br> element
        parentRun.removeChild(br);
      }
      foundPageBreaks++;
    }
  }

  // 2. Find section breaks with page break type
  const paragraphs = Array.from(doc.getElementsByTagNameNS(WORDPROCESSING_NS, "p"));
  const paragraphsNoNs = Array.from(doc.getElementsByTagName("w:p"));
  const allParagraphs = Array.from(new Set([...paragraphs, ...paragraphsNoNs]));

  for (const para of allParagraphs) {
    const pPr = para.getElementsByTagNameNS(WORDPROCESSING_NS, "pPr")[0] ||
                para.getElementsByTagName("w:pPr")[0];
    if (!pPr) continue;

    const sectPr = pPr.getElementsByTagNameNS(WORDPROCESSING_NS, "sectPr")[0] ||
                   pPr.getElementsByTagName("w:sectPr")[0];
    if (!sectPr) continue;

    const typeEl = sectPr.getElementsByTagNameNS(WORDPROCESSING_NS, "type")[0] ||
                   sectPr.getElementsByTagName("w:type")[0];
    if (!typeEl) continue;

    const val = typeEl.getAttribute("w:val") ||
                typeEl.getAttributeNS(WORDPROCESSING_NS, "val") ||
                getAttr(typeEl, "val");
    if (val === "nextPage" || val === "oddPage" || val === "evenPage") {
      // Append a marker run at the end of this paragraph
      para.appendChild(createMarkerRun());
      foundPageBreaks++;
    }
  }

  if (foundPageBreaks > 0) {
    // Serialize back and update the ZIP
    const serializer = new XMLSerializer();
    const modifiedXml = serializer.serializeToString(doc);
    zip.file("word/document.xml", modifiedXml);
  }

  return { hasPageBreaks: foundPageBreaks > 0 };
}

/**
 * Convert page break markers in HTML to actual page break elements.
 * The markers were injected into the OOXML before mammoth conversion,
 * so they appear in the HTML at the correct positions.
 */
function convertPageBreakMarkersToHtml(html: string): string {
  // The marker might be inside a <p> tag or other element.
  // We need to:
  // 1. If the marker is the only content of a <p>, replace the entire <p> with an <hr>
  // 2. If it's within text, split at the marker and insert the <hr>

  // Handle case: marker is inside a paragraph (possibly with whitespace only)
  // Replace <p>...\uE003...</p> patterns
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstElementChild as HTMLElement;

  // Find all text nodes containing the marker
  const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const markerNodes: Text[] = [];
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    if (node.textContent?.includes(PB_MARKER)) {
      markerNodes.push(node);
    }
  }

  for (const textNode of markerNodes) {
    const text = textNode.textContent || "";
    const parts = text.split(PB_MARKER);

    const parentElement = textNode.parentNode as HTMLElement;
    if (!parentElement) continue;

    // Create a document fragment with the parts and page breaks
    const fragment = doc.createDocumentFragment();

    for (let i = 0; i < parts.length; i++) {
      if (parts[i]) {
        fragment.appendChild(doc.createTextNode(parts[i]));
      }
      if (i < parts.length - 1) {
        // Insert the page break HR element
        // We need to close the current block element, insert HR, and start a new one
        // For simplicity, insert a placeholder that we'll handle next
        const marker = doc.createElement("span");
        marker.setAttribute("data-page-break-marker", "true");
        fragment.appendChild(marker);
      }
    }

    parentElement.replaceChild(fragment, textNode);
  }

  // Now find all the placeholder spans and replace them with proper page break HRs
  // We need to "break out" of the parent paragraph
  const markers = container.querySelectorAll("[data-page-break-marker]");
  for (const marker of Array.from(markers)) {
    const parentBlock = findParentBlock(marker as HTMLElement, container);

    if (parentBlock && parentBlock !== container) {
      const hr = doc.createElement("hr");
      hr.className = "page-break";
      hr.setAttribute("data-type", "page-break");

      // Check if the marker's parent block is effectively empty (only the marker)
      const blockText = (parentBlock.textContent || "").trim();
      if (!blockText) {
        // The paragraph is empty - just replace it with the HR
        parentBlock.parentNode?.replaceChild(hr, parentBlock);
      } else {
        // There is other text. Insert the HR after the parent block.
        parentBlock.parentNode?.insertBefore(hr, parentBlock.nextSibling);
        // Remove the marker span
        marker.parentNode?.removeChild(marker);
      }
    } else {
      // Marker is at top level, just replace with HR
      const hr = doc.createElement("hr");
      hr.className = "page-break";
      hr.setAttribute("data-type", "page-break");
      marker.parentNode?.replaceChild(hr, marker);
    }
  }

  return container.innerHTML;
}

/**
 * Find the nearest block-level parent element of a node
 */
function findParentBlock(node: HTMLElement, root: HTMLElement): HTMLElement | null {
  const blockTags = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "DIV", "LI", "BLOCKQUOTE", "PRE"]);
  let current = node.parentElement;
  while (current && current !== root) {
    if (blockTags.has(current.tagName)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Map Word border styles to CSS
 */
const BORDER_STYLE_MAP: Record<string, string> = {
  "single": "solid",
  "thick": "solid",
  "double": "double",
  "dotted": "dotted",
  "dashed": "dashed",
  "dashDotStroked": "dashed",
  "threeDEmboss": "ridge",
  "threeDEngrave": "groove",
  "nil": "none",
  "none": "none",
};

/**
 * Extract table formatting from document.xml
 */
async function extractTableFormatting(zip: JSZip): Promise<TableFormatting[]> {
  const tables: TableFormatting[] = [];
  
  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) return tables;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, "text/xml");
  
  // Find all table elements
  const tableElements = doc.getElementsByTagName("w:tbl");
  
  for (let tableIdx = 0; tableIdx < tableElements.length; tableIdx++) {
    const table = tableElements[tableIdx];
    const tableFormatting: TableFormatting = {
      tableIndex: tableIdx,
      cells: [],
    };
    
    // Find all rows in this table
    const rows = table.getElementsByTagName("w:tr");
    
    for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
      const row = rows[rowIdx];
      
      // Find all cells in this row
      const cells = row.getElementsByTagName("w:tc");
      
      for (let cellIdx = 0; cellIdx < cells.length; cellIdx++) {
        const cell = cells[cellIdx];
        const cellFormatting: TableCellFormatting = {
          rowIndex: rowIdx,
          cellIndex: cellIdx,
        };
        
        // Get cell properties (w:tcPr)
        const tcPr = cell.getElementsByTagName("w:tcPr")[0];
        if (tcPr) {
          // Get shading/background color (w:shd)
          const shd = tcPr.getElementsByTagName("w:shd")[0];
          if (shd) {
            const fill = getAttr(shd, "fill");
            if (fill && fill !== "auto" && fill !== "FFFFFF") {
              cellFormatting.backgroundColor = `#${fill}`;
            }
          }
          
          // Get cell borders (w:tcBorders)
          const tcBorders = tcPr.getElementsByTagName("w:tcBorders")[0];
          if (tcBorders) {
            // Parse each border direction
            const parseBorder = (borderEl: Element | undefined) => {
              if (!borderEl) return undefined;
              const color = getAttr(borderEl, "color") || "000000";
              const sz = getAttr(borderEl, "sz");
              const val = getAttr(borderEl, "val");
              
              if (val === "nil" || val === "none") return undefined;
              
              // sz is in eighths of a point
              const width = sz ? parseInt(sz, 10) / 8 : 1;
              const style = BORDER_STYLE_MAP[val || "single"] || "solid";
              
              return { color: `#${color}`, width, style };
            };
            
            cellFormatting.borderTop = parseBorder(tcBorders.getElementsByTagName("w:top")[0]);
            cellFormatting.borderBottom = parseBorder(tcBorders.getElementsByTagName("w:bottom")[0]);
            cellFormatting.borderLeft = parseBorder(tcBorders.getElementsByTagName("w:left")[0]);
            cellFormatting.borderRight = parseBorder(tcBorders.getElementsByTagName("w:right")[0]);
          }
          
          // Get vertical alignment (w:vAlign)
          const vAlign = tcPr.getElementsByTagName("w:vAlign")[0];
          if (vAlign) {
            const val = getAttr(vAlign, "val");
            if (val) {
              const vAlignMap: Record<string, string> = {
                "top": "top",
                "center": "middle",
                "bottom": "bottom",
              };
              cellFormatting.verticalAlign = vAlignMap[val] || val;
            }
          }
          
          // Get cell width (w:tcW)
          const tcW = tcPr.getElementsByTagName("w:tcW")[0];
          if (tcW) {
            const w = getAttr(tcW, "w");
            const type = getAttr(tcW, "type");
            if (w && type === "dxa") {
              // dxa = twips (1/20 of a point)
              cellFormatting.width = parseInt(w, 10) / 20;
            }
          }
        }
        
        // Only add if there's some formatting
        if (cellFormatting.backgroundColor || cellFormatting.borderTop || 
            cellFormatting.borderBottom || cellFormatting.borderLeft ||
            cellFormatting.borderRight || cellFormatting.verticalAlign) {
          tableFormatting.cells.push(cellFormatting);
        }
      }
    }
    
    if (tableFormatting.cells.length > 0) {
      tables.push(tableFormatting);
    }
  }
  
  return tables;
}

/**
 * Apply table formatting to HTML output
 * Adds background colors and borders to table cells
 */
function applyTableFormattingToHtml(html: string, tables: TableFormatting[]): string {
  if (tables.length === 0) return html;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstElementChild as HTMLElement;
  
  // Find all tables in the HTML
  const htmlTables = container.querySelectorAll("table");
  
  for (let tableIdx = 0; tableIdx < htmlTables.length && tableIdx < tables.length; tableIdx++) {
    const htmlTable = htmlTables[tableIdx];
    const tableFormat = tables[tableIdx];
    
    if (!tableFormat) continue;
    
    // Find all rows
    const rows = htmlTable.querySelectorAll("tr");
    
    for (const cellFormat of tableFormat.cells) {
      const row = rows[cellFormat.rowIndex];
      if (!row) continue;
      
      // Find the cell (could be td or th)
      const cells = row.querySelectorAll("td, th");
      const cell = cells[cellFormat.cellIndex] as HTMLElement;
      if (!cell) continue;
      
      // Apply background color
      if (cellFormat.backgroundColor) {
        cell.style.backgroundColor = cellFormat.backgroundColor;
      }
      
      // Apply borders
      if (cellFormat.borderTop) {
        cell.style.borderTop = `${cellFormat.borderTop.width}pt ${cellFormat.borderTop.style} ${cellFormat.borderTop.color}`;
      }
      if (cellFormat.borderBottom) {
        cell.style.borderBottom = `${cellFormat.borderBottom.width}pt ${cellFormat.borderBottom.style} ${cellFormat.borderBottom.color}`;
      }
      if (cellFormat.borderLeft) {
        cell.style.borderLeft = `${cellFormat.borderLeft.width}pt ${cellFormat.borderLeft.style} ${cellFormat.borderLeft.color}`;
      }
      if (cellFormat.borderRight) {
        cell.style.borderRight = `${cellFormat.borderRight.width}pt ${cellFormat.borderRight.style} ${cellFormat.borderRight.color}`;
      }
      
      // Apply vertical alignment
      if (cellFormat.verticalAlign) {
        cell.style.verticalAlign = cellFormat.verticalAlign;
      }
    }
  }
  
  return container.innerHTML;
}

/**
 * Apply run-level formatting to HTML output
 * Injects inline styles for text color, highlight, font size, subscript, superscript
 */
function applyRunFormattingToHtml(html: string, runs: RunFormatting[]): string {
  // Process each formatted run
  for (const run of runs) {
    if (!run.text || run.text.length < 2) continue; // Skip very short text to avoid false matches
    
    // Build inline style
    const styles: string[] = [];
    if (run.color) {
      styles.push(`color: #${run.color}`);
    }
    if (run.highlight) {
      styles.push(`background-color: ${run.highlight}`);
    }
    if (run.fontFamily) {
      // Use supported font if we have it, else Times New Roman
      const resolvedFont = resolveFontFamily(run.fontFamily);
      styles.push(`font-family: ${resolvedFont}`);
    }
    if (run.fontSize) {
      styles.push(`font-size: ${run.fontSize}pt`);
    }
    
    // Escape special regex characters in the text
    const escapedText = escapeRegExp(run.text);
    
    // Handle subscript and superscript by wrapping in <sub> or <sup>
    if (run.subscript) {
      // Check if already wrapped in <sub>
      if (!html.includes(`<sub>${run.text}</sub>`) && !html.includes(`<sub>${escapedText}</sub>`)) {
        const regex = new RegExp(`(?<!<sub>)(${escapedText})(?!</sub>)`, "g");
        let replaced = false;
        html = html.replace(regex, (match) => {
          if (replaced) return match;
          replaced = true;
          return `<sub>${match}</sub>`;
        });
      }
    } else if (run.superscript) {
      // Check if already wrapped in <sup>
      if (!html.includes(`<sup>${run.text}</sup>`) && !html.includes(`<sup>${escapedText}</sup>`)) {
        const regex = new RegExp(`(?<!<sup>)(${escapedText})(?!</sup>)`, "g");
        let replaced = false;
        html = html.replace(regex, (match) => {
          if (replaced) return match;
          replaced = true;
          return `<sup>${match}</sup>`;
        });
      }
    }
    
    // Apply inline styles if we have any
    if (styles.length > 0) {
      const styleAttr = styles.join("; ");
      // Split HTML into tag and text parts so we only match within text content,
      // not inside HTML tags or attributes (which would break the markup).
      const parts = html.split(/(<[^>]*>)/);
      let replaced = false;
      for (let i = 0; i < parts.length && !replaced; i++) {
        // Skip tag parts (odd indices from the split, or parts starting with <)
        if (parts[i].startsWith("<")) continue;
        // Decode HTML entities for comparison
        const decoded = parts[i]
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&nbsp;/g, "\u00A0");
        const idx = decoded.indexOf(run.text);
        if (idx !== -1) {
          // Find the corresponding position in the original (encoded) text.
          // Re-encode the portion boundaries by measuring from the original part.
          // Since common entities expand by a fixed amount we walk both strings.
          let origIdx = 0;
          let decIdx = 0;
          while (decIdx < idx && origIdx < parts[i].length) {
            if (parts[i][origIdx] === "&") {
              const semi = parts[i].indexOf(";", origIdx);
              if (semi !== -1) {
                origIdx = semi + 1;
                decIdx++;
                continue;
              }
            }
            origIdx++;
            decIdx++;
          }
          const startOrig = origIdx;
          let remaining = run.text.length;
          while (remaining > 0 && origIdx < parts[i].length) {
            if (parts[i][origIdx] === "&") {
              const semi = parts[i].indexOf(";", origIdx);
              if (semi !== -1) {
                origIdx = semi + 1;
                remaining--;
                continue;
              }
            }
            origIdx++;
            remaining--;
          }
          const endOrig = origIdx;
          const matchedOrig = parts[i].substring(startOrig, endOrig);
          parts[i] =
            parts[i].substring(0, startOrig) +
            `<span style="${styleAttr}">${matchedOrig}</span>` +
            parts[i].substring(endOrig);
          replaced = true;
        }
      }
      if (replaced) {
        html = parts.join("");
      }
    }
  }
  
  return html;
}

/**
 * Generate a unique track change ID for TipTap
 */
function generateTrackChangeId(): string {
  return `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract comments from word/comments.xml
 */
async function extractCommentsFromXml(zip: JSZip): Promise<RawComment[]> {
  const commentsXml = await zip.file("word/comments.xml")?.async("string");
  if (!commentsXml) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(commentsXml, "text/xml");
  
  // Handle XML namespace - comments are in w:comment elements
  const comments: RawComment[] = [];
  
  // Try both with and without namespace
  let commentNodes = doc.getElementsByTagName("w:comment");
  if (commentNodes.length === 0) {
    commentNodes = doc.getElementsByTagNameNS(
      "http://schemas.openxmlformats.org/wordprocessingml/2006/main",
      "comment"
    );
  }

  for (let i = 0; i < commentNodes.length; i++) {
    const node = commentNodes[i];
    const id = node.getAttribute("w:id") || node.getAttribute("id") || String(i);
    const author = node.getAttribute("w:author") || node.getAttribute("author") || "Unknown";
    const date = node.getAttribute("w:date") || node.getAttribute("date") || new Date().toISOString();
    
    // Get text content from all w:t elements within the comment
    let content = "";
    const textNodes = node.getElementsByTagName("w:t");
    for (let j = 0; j < textNodes.length; j++) {
      content += textNodes[j].textContent || "";
    }
    // Fallback to direct text content
    if (!content) {
      content = node.textContent?.trim() || "";
    }

    comments.push({ id, author, date, content });
  }

  return comments;
}

/**
 * Generate a unique comment ID for TipTap
 */
function generateCommentId(): string {
  return `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Escape HTML attribute value
 */
function escapeHtmlAttr(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Apply paragraph styles (alignment, etc.) to mammoth HTML output
 * This post-processes the HTML to add alignment and other styles
 */
/**
 * Pre-calculate all numbering strings from OOXML paragraph styles
 * This ensures correct numbering even if HTML matching fails for some paragraphs
 */
function preCalculateNumbering(
  paragraphStyles: ParagraphStyle[],
  numberingDefs: Map<string, NumberingDefinition>
): Map<number, { numberingStr: string; level: number; numId: string }> {
  const numberedParagraphs = new Map<number, { numberingStr: string; level: number; numId: string }>();
  const listCounters: Map<string, number[]> = new Map();
  
  for (let idx = 0; idx < paragraphStyles.length; idx++) {
    const style = paragraphStyles[idx];
    
    if (style.numId && style.listLevel !== undefined) {
      const numDef = numberingDefs.get(style.numId);
      if (numDef && isOutlineNumbering(numDef)) {
        let counters = listCounters.get(style.numId) || [];
        const level = style.listLevel;
        
        // When going to a higher level (lower number), reset sub-level counters
        if (counters.length > level + 1) {
          counters = counters.slice(0, level + 1);
        }
        
        // Initialize counters up to current level
        while (counters.length <= level) {
          const startVal = numDef.levels[counters.length]?.start || 1;
          counters.push(startVal - 1);
        }
        
        // Increment current level counter
        counters[level]++;
        
        // Reset all sub-level counters
        for (let subLevel = level + 1; subLevel < counters.length; subLevel++) {
          const startVal = numDef.levels[subLevel]?.start || 1;
          counters[subLevel] = startVal - 1;
        }
        
        listCounters.set(style.numId, [...counters]); // Clone to avoid reference issues
        
        // Build the numbering string
        const levelDef = numDef.levels[level];
        if (levelDef) {
          let numberingStr = levelDef.text;
          for (let i = 0; i <= level; i++) {
            numberingStr = numberingStr.replace(`%${i + 1}`, String(counters[i] || 1));
          }
          numberedParagraphs.set(idx, { numberingStr, level, numId: style.numId });
        }
      }
    }
  }
  
  return numberedParagraphs;
}

function applyParagraphStylesToHtml(
  html: string,
  paragraphStyles: ParagraphStyle[],
  numberingDefs: Map<string, NumberingDefinition>
): string {
  // Pre-calculate ALL numbering from OOXML first
  // This ensures correct numbers even if HTML matching fails for some paragraphs
  const preCalculatedNumbers = preCalculateNumbering(paragraphStyles, numberingDefs);
  
  // Parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstElementChild as HTMLElement;
  
  // Helper function to get DIRECT text content of an element, excluding nested lists
  // This is important because mammoth creates nested <ol>/<ul> inside <li>, and
  // textContent would include ALL nested text, causing matching failures
  const getDirectTextContent = (element: HTMLElement): string => {
    let text = "";
    for (let i = 0; i < element.childNodes.length; i++) {
      const node = element.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        // Direct text node
        text += node.textContent || "";
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = (node as HTMLElement).tagName?.toLowerCase();
        // Skip nested lists - their content belongs to separate paragraphs
        if (tagName !== "ol" && tagName !== "ul") {
          // Recursively get text from inline elements (strong, em, span, a, etc.)
          text += getDirectTextContent(node as HTMLElement);
        }
      }
    }
    return text;
  };
  
  // Get all block elements (paragraphs, headings, list items)
  // Store both the element and its direct text content for matching
  const blockElements: { element: HTMLElement; directText: string }[] = [];
  const collectBlocks = (element: Element) => {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i] as HTMLElement;
      if (child.nodeType === 1) {
        const tagName = child.tagName?.toLowerCase();
        if (tagName === "p" || tagName?.match(/^h[1-6]$/)) {
          blockElements.push({ 
            element: child, 
            directText: child.textContent || "" 
          });
        } else if (tagName === "ol" || tagName === "ul") {
          // Recursively collect from lists
          collectBlocks(child);
        } else if (tagName === "li") {
          // Check for paragraph inside list item
          const liPara = child.querySelector("p");
          if (liPara) {
            blockElements.push({ 
              element: liPara as HTMLElement, 
              directText: liPara.textContent || "" 
            });
          } else {
            // The li itself acts as the block - use DIRECT text only!
            blockElements.push({ 
              element: child, 
              directText: getDirectTextContent(child) 
            });
          }
          // Also recursively process nested lists within this li
          const nestedLists = child.querySelectorAll(":scope > ol, :scope > ul");
          nestedLists.forEach(nestedList => collectBlocks(nestedList));
        } else if (tagName === "blockquote" || tagName === "div") {
          collectBlocks(child);
        }
      }
    }
  };
  collectBlocks(container);
  
  
  // Helper function to normalize text for comparison
  // This handles whitespace differences that can occur due to mammoth's HTML generation
  // (e.g., <strong>text</strong>.<strong> </strong>more creates extra spaces)
  // Also strips track change markers and deletion text so matching works correctly
  const normalizeText = (text: string): string => {
    return stripTrackChangeMarkers(text)
      .trim()
      .replace(/\s+/g, ' ')  // Collapse multiple whitespace to single space
      .toLowerCase();         // Case-insensitive matching
  };
  
  // Build a map of normalized text content to paragraph style index for faster lookup
  // Include all paragraphs with meaningful content (5+ chars) to catch short headings like "Definitions."
  const textToStyleIndex = new Map<string, number>();
  for (let i = 0; i < paragraphStyles.length; i++) {
    const style = paragraphStyles[i];
    if (style.textContent && style.textContent.trim().length >= 5) {
      // Use full normalized text (up to 80 chars) as key
      const key = normalizeText(style.textContent).substring(0, 80);
      if (!textToStyleIndex.has(key)) {
        textToStyleIndex.set(key, i);
      }
    }
  }
  
  // Track which styles have been matched
  const matchedStyles = new Set<number>();
  
  // Apply styles to matching paragraphs using text-based lookup
  for (const block of blockElements) {
    const { element, directText } = block;
    const elementText = directText.trim();
    if (elementText.length < 5) continue; // Skip very short content
    
    // Normalize the element text for comparison
    const normalizedElementText = normalizeText(elementText);
    
    // Find matching style by text content
    let matchingStyleIndex: number | undefined;
    
    // Try exact prefix match (80 chars)
    const lookupKey = normalizedElementText.substring(0, 80);
    if (textToStyleIndex.has(lookupKey)) {
      matchingStyleIndex = textToStyleIndex.get(lookupKey);
    }
    
    // Try shorter prefix match (40 chars) if exact didn't work
    if (matchingStyleIndex === undefined) {
      const shortKey = normalizedElementText.substring(0, 40);
      const entries = Array.from(textToStyleIndex.entries());
      for (let i = 0; i < entries.length; i++) {
        const [key, idx] = entries[i];
        if (key.substring(0, 40) === shortKey && !matchedStyles.has(idx)) {
          matchingStyleIndex = idx;
          break;
        }
      }
    }
    
    // Try even shorter match (20 chars) for medium paragraphs
    if (matchingStyleIndex === undefined && normalizedElementText.length >= 20) {
      const veryShortKey = normalizedElementText.substring(0, 20);
      for (let i = 0; i < paragraphStyles.length; i++) {
        if (matchedStyles.has(i)) continue;
        const style = paragraphStyles[i];
        if (style.textContent && normalizeText(style.textContent).substring(0, 20) === veryShortKey) {
          matchingStyleIndex = i;
          break;
        }
      }
    }
    
    // Try full text match for short headings (5-19 chars) like "Definitions.", "Security.", etc.
    if (matchingStyleIndex === undefined && normalizedElementText.length >= 5 && normalizedElementText.length < 20) {
      for (let i = 0; i < paragraphStyles.length; i++) {
        if (matchedStyles.has(i)) continue;
        const style = paragraphStyles[i];
        if (style.textContent) {
          const normalizedStyleText = normalizeText(style.textContent);
          // For short texts, require exact match or that one starts with the other
          if (normalizedStyleText === normalizedElementText ||
              normalizedStyleText.startsWith(normalizedElementText) ||
              normalizedElementText.startsWith(normalizedStyleText)) {
            matchingStyleIndex = i;
            break;
          }
        }
      }
    }
    
    if (matchingStyleIndex === undefined) {
      // Debug: log unmatched paragraphs that might be important
      if (normalizedElementText.length > 30) {
      }
      continue;
    }
    
    matchedStyles.add(matchingStyleIndex);
    const matchingStyle = paragraphStyles[matchingStyleIndex];
    
    // Apply alignment
    if (matchingStyle.alignment && matchingStyle.alignment !== "left") {
      element.style.textAlign = matchingStyle.alignment;
    }
    
    // Apply line spacing
    if (matchingStyle.lineSpacing && matchingStyle.lineSpacing !== 1) {
      element.style.lineHeight = String(matchingStyle.lineSpacing);
    }
    
    // Apply paragraph spacing
    if (matchingStyle.spaceBefore && matchingStyle.spaceBefore > 0) {
      element.style.marginTop = `${matchingStyle.spaceBefore}pt`;
    }
    if (matchingStyle.spaceAfter && matchingStyle.spaceAfter > 0) {
      element.style.marginBottom = `${matchingStyle.spaceAfter}pt`;
    }
    
    // Apply indentation
    if (matchingStyle.leftIndent && matchingStyle.leftIndent > 0) {
      element.style.marginLeft = `${matchingStyle.leftIndent}pt`;
    }
    if (matchingStyle.rightIndent && matchingStyle.rightIndent > 0) {
      element.style.marginRight = `${matchingStyle.rightIndent}pt`;
    }
    if (matchingStyle.firstLineIndent) {
      element.style.textIndent = `${matchingStyle.firstLineIndent}pt`;
    }
    
    // Apply pre-calculated numbering (this ensures correct numbers!)
    const numbering = preCalculatedNumbers.get(matchingStyleIndex);
    if (numbering) {
      // Debug: log successful matches for numbered paragraphs
      element.setAttribute("data-outline-level", String(numbering.level));
      element.setAttribute("data-outline-num-id", numbering.numId);
      element.setAttribute("data-outline-number", numbering.numberingStr);
    }
  }
  
  
  return container.innerHTML;
}

/**
 * Convert mammoth's list output to outline-numbered lists
 * This transforms <ol><li> structures into <div><p> elements with number prefixes
 * 
 * Key insight: mammoth creates nested <ol><li> structures, but:
 * 1. When lists are broken by non-numbered paragraphs, mammoth starts a NEW <ol>
 * 2. TipTap re-renders HTML and applies its own list numbering
 * 3. Inline styles like list-style-type:none don't survive TipTap parsing
 * 
 * Solution: 
 * 1. Convert all <ol> to <div> (removes list behavior entirely)
 * 2. Convert all <li> to <p> with our pre-calculated numbers as text prefixes
 * This preserves document order while removing all list auto-numbering.
 */
function convertToOutlineNumbering(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstElementChild as HTMLElement;
  
  // Find all elements with outline numbering data
  const outlineElements = container.querySelectorAll("[data-outline-number]");
  
  
  // Step 1: Add number prefixes to all numbered elements (in document order)
  outlineElements.forEach((el) => {
    const number = el.getAttribute("data-outline-number");
    const level = parseInt(el.getAttribute("data-outline-level") || "0", 10);
    
    if (number) {
      // Add the number prefix as the first child
      const numberSpan = doc.createElement("span");
      numberSpan.className = "outline-number";
      numberSpan.textContent = number + " ";
      el.insertBefore(numberSpan, el.firstChild);
      
      // Add styling
      el.classList.add("outline-numbered", `outline-level-${level}`);
      (el as HTMLElement).style.marginLeft = `${level * 24}px`;
    }
  });
  
  // Step 2: Convert all <ol> and <ul> to <div> to remove list behavior
  // This must be done AFTER adding prefixes to preserve element references
  const allLists = container.querySelectorAll("ol, ul");
  allLists.forEach((list) => {
    const div = doc.createElement("div");
    div.className = "outline-list-container";
    
    // Move all children to the new div
    while (list.firstChild) {
      div.appendChild(list.firstChild);
    }
    
    // Replace the list with the div
    list.parentElement?.replaceChild(div, list);
  });
  
  // Step 3: Convert all <li> to <p>
  const allListItems = container.querySelectorAll("li");
  allListItems.forEach((li) => {
    const p = doc.createElement("p");
    
    // Copy classes and styles
    p.className = li.className;
    p.setAttribute("style", li.getAttribute("style") || "");
    
    // Copy data attributes
    Array.from(li.attributes).forEach(attr => {
      if (attr.name.startsWith("data-")) {
        p.setAttribute(attr.name, attr.value);
      }
    });
    
    // Move all children to the new p
    while (li.firstChild) {
      p.appendChild(li.firstChild);
    }
    
    // Replace li with p
    li.parentElement?.replaceChild(p, li);
  });
  
  return container.innerHTML;
}

/**
 * Import a DOCX file and convert to HTML with native TipTap comments and track changes
 * 
 * @param file - The DOCX file to import
 * @returns Promise with HTML content, warnings, track changes, and comments
 */
export async function importDocxToHtml(
  file: File,
  options?: { deferCommentConversion?: boolean }
): Promise<DocxImportResult> {
  const arrayBuffer = await file.arrayBuffer();
  
  // Load the DOCX as a ZIP
  const zip = await JSZip.loadAsync(arrayBuffer);
  
  // ====================================================================
  // STEP 1: Extract comment metadata from word/comments.xml
  // ====================================================================
  const rawComments = await extractCommentsFromXml(zip);

  // ====================================================================
  // STEP 1.5: Pre-process OOXML for comments
  // ====================================================================
  // Inject invisible Unicode markers at w:commentRangeStart / w:commentRangeEnd
  // positions. Markers are <w:r><w:t> text runs so mammoth outputs them as
  // regular text. Must run BEFORE track-change preprocessing because that
  // step also modifies document.xml.
  const { commentMarkerMeta, hasComments } = await preprocessDocxForComments(zip, rawComments);

  // ====================================================================
  // STEP 2: Pre-process OOXML for track changes
  // ====================================================================
  // This modifies document.xml IN PLACE to inject invisible markers around
  // insertions and make deletion text visible. The markers flow through
  // mammoth's conversion and appear in the HTML at the correct positions.
  const { markerMeta, hasTrackChanges } = await preprocessDocxForTrackChanges(zip);

  // ====================================================================
  // STEP 2.5: Pre-process OOXML for page breaks
  // ====================================================================
  // Replace <w:br w:type="page"/> and section page breaks with Unicode
  // markers that flow through mammoth and end up in the HTML at the
  // correct positions.
  const { hasPageBreaks } = await preprocessDocxForPageBreaks(zip);

  // ====================================================================
  // STEP 3: Extract formatting data from the (now modified) ZIP
  // ====================================================================
  // We extract from the modified ZIP so that paragraph text content
  // includes the markers (matching mammoth's output). This ensures
  // text-based matching between OOXML and HTML works correctly.
  const [paragraphStyles, numberingDefs, runFormatting, tableFormatting] = await Promise.all([
    extractParagraphStyles(zip),
    extractNumberingDefinitions(zip),
    extractRunFormatting(zip),
    extractTableFormatting(zip),
  ]);

  // ====================================================================
  // STEP 4: Convert to HTML with mammoth
  // ====================================================================
  // Use the modified ZIP (with comment + track change + page break markers)
  // so mammoth includes all text with markers at correct positions.
  // If no preprocessing modified the XML, use the original arrayBuffer
  // to avoid the overhead of re-generating the ZIP.
  const mammothArrayBuffer = (hasComments || hasTrackChanges || hasPageBreaks)
    ? await zip.generateAsync({ type: "arraybuffer" })
    : arrayBuffer;
  // mammoth's browser build expects { arrayBuffer }, but its Node.js build
  // (used in Jest/jsdom) expects { buffer }. Pass both for compatibility.
  const mammothInput: any = { arrayBuffer: mammothArrayBuffer };
  if (typeof Buffer !== "undefined") {
    mammothInput.buffer = Buffer.from(mammothArrayBuffer);
  }
  // Custom image converter: embed all image formats as base64.
  // The server will convert unsupported formats (EMF/WMF/TIFF) during
  // GCS upload. Large images are also handled server-side with compression.
  const convertImage = mammoth.images.imgElement(function (image: any) {
    const contentType: string = (image.contentType || "image/png").toLowerCase();

    return image.read("base64").then(function (imageBuffer: string) {
      // Cap embedded images at 5 MB of base64 to avoid blowing up the HTML
      // string, the JSON representation, and the save payload.
      // Large images will be compressed server-side during GCS upload.
      const MAX_BASE64_LENGTH = 5 * 1024 * 1024;
      if (imageBuffer.length > MAX_BASE64_LENGTH) {
      }
      return {
        src: "data:" + contentType + ";base64," + imageBuffer,
      };
    });
  });

  const result = await mammoth.convertToHtml(
    mammothInput,
    {
      convertImage: convertImage,
      styleMap: [
        "p[style-name='Heading 1'] => h1:fresh",
        "p[style-name='Heading 2'] => h2:fresh",
        "p[style-name='Heading 3'] => h3:fresh",
        "p[style-name='Heading 4'] => h4:fresh",
        "p[style-name='Heading 5'] => h5:fresh",
        "p[style-name='Heading 6'] => h6:fresh",
        "p[style-name='Title'] => h1:fresh",
        "p[style-name='Subtitle'] => h2:fresh",
        "u => u",
      ],
      includeDefaultStyleMap: true,
    }
  );
  
  let html = result.value;
  let comments: ImportedComment[] = [];
  let trackChanges: ImportedTrackChange[] = [];

  // ====================================================================
  // STEP 5: Apply formatting post-processing
  // ====================================================================
  // All text matching works because both OOXML text and HTML text contain
  // the same markers. The markers are invisible Unicode chars that don't
  // affect visual matching.

  if (paragraphStyles.length > 0) {
    html = applyParagraphStylesToHtml(html, paragraphStyles, numberingDefs);

  }

  if (html.includes("data-outline-number")) {
    html = convertToOutlineNumbering(html);

  }

  if (runFormatting.length > 0) {
    html = applyRunFormattingToHtml(html, runFormatting);

  }

  if (tableFormatting.length > 0) {
    html = applyTableFormattingToHtml(html, tableFormatting);

  }

  if (hasPageBreaks) {
    html = convertPageBreakMarkersToHtml(html);

  }

  // ====================================================================
  // STEP 6: Convert markers to track change <span> elements
  // ====================================================================
  // This is the final step — markers are now correctly positioned within
  // the fully-formatted HTML. Convert them to spans.
  if (hasTrackChanges && markerMeta.size > 0) {
    const converted = convertMarkersToTrackChangeSpans(html, markerMeta);
    html = converted.html;
    trackChanges = converted.trackChanges;

  }

  // ====================================================================
  // STEP 7: Normalize images for TipTap's block-level Image extension
  // ====================================================================
  // Mammoth outputs <img> tags inside <p> tags, but TipTap's Image extension
  // is configured with inline: false (block-level).  ProseMirror's parser
  // silently drops all content after a block-level node found inside an
  // inline context (<p>).  Lift images out of paragraphs to prevent this.
  //
  // IMPORTANT: This step does a DOMParser round-trip when images are present.
  // It MUST run BEFORE comment span conversion because the DOM round-trip
  // can restructure/empty <span> elements that cross formatting boundaries.
  // Unicode comment markers (\uE000…) survive as plain text in text nodes.
  html = normalizeImagesForTipTap(html);

  // ====================================================================
  // STEP 8: Convert comment markers to <span data-comment-id> elements
  // ====================================================================
  // Same principle as track change marker conversion — markers were injected
  // into the OOXML, flowed through mammoth, and are now in the HTML at the
  // correct positions. Convert them to TipTap-compatible comment spans.
  //
  // When deferCommentConversion is true, skip this step — the caller will
  // run convertCommentMarkersToSpans() after any further DOM processing
  // (e.g. uploadDocxImagesToGCS which does another DOMParser round-trip).
  if (hasComments && commentMarkerMeta.size > 0 && !options?.deferCommentConversion) {
    const converted = convertCommentMarkersToSpans(html, commentMarkerMeta);
    html = converted.html;
    comments = converted.comments;
  }
  
  return {
    html,
    messages: result.messages.map((m) => m.message),
    hasTrackChanges,
    comments,
    trackChanges,
    commentMarkerMeta: hasComments ? commentMarkerMeta : undefined,
  };
}

/**
 * Normalize <img> tags in HTML so they are block-level elements, not nested
 * inside <p> tags.  TipTap's Image extension is configured with `inline: false`
 * (block-level), but mammoth outputs images inside <p> tags.  When ProseMirror's
 * parser encounters a block-level node inside an inline context, it can fail to
 * recover and silently drops all content AFTER the image.
 *
 * This function:
 * 1. If <img> is the sole meaningful child of a <p>, replaces the <p> with the <img>.
 * 2. If <img> is mixed with text in a <p>, splits into: <p>before</p><img><p>after</p>.
 *
 * Call this before passing HTML to TipTap's editor.
 */
export function normalizeImagesForTipTap(html: string): string {
  if (!html.includes("<img")) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const images = doc.querySelectorAll("img");

  for (const img of Array.from(images)) {
    const parent = img.parentElement;
    if (!parent) continue;

    // Only fix images that are direct children of block containers like <p>
    // Leave images in table cells, list items, etc. alone — those contexts
    // can contain block-level children in TipTap's schema.
    const parentTag = parent.tagName.toUpperCase();
    if (parentTag !== "P") continue;

    // Check if the image is the sole meaningful child (ignoring whitespace)
    const siblings = Array.from(parent.childNodes).filter(
      (node) =>
        node !== img &&
        !(node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
    );

    if (siblings.length === 0) {
      // Image is the only content in the <p> — lift it out
      parent.replaceWith(img);
    } else {
      // Image is mixed with text/other elements — split the paragraph
      const before = doc.createElement("p");
      const after = doc.createElement("p");

      // Copy the parent's attributes (e.g. style, class) to both halves
      for (const attr of Array.from(parent.attributes)) {
        before.setAttribute(attr.name, attr.value);
        after.setAttribute(attr.name, attr.value);
      }

      let reachedImage = false;
      for (const child of Array.from(parent.childNodes)) {
        if (child === img) {
          reachedImage = true;
          continue;
        }
        if (reachedImage) {
          after.appendChild(child.cloneNode(true));
        } else {
          before.appendChild(child.cloneNode(true));
        }
      }

      // Build the replacement fragment
      const fragment = doc.createDocumentFragment();
      if (before.textContent?.trim() || before.querySelector("*")) {
        fragment.appendChild(before);
      }
      fragment.appendChild(img.cloneNode(true));
      if (after.textContent?.trim() || after.querySelector("*")) {
        fragment.appendChild(after);
      }

      parent.replaceWith(fragment);
    }
  }

  return doc.body.innerHTML;
}

/**
 * Post-process mammoth HTML: upload every base64 <img> to GCS and replace
 * the src with a signed URL.  Adds a `data-gcs-file-name` attribute so
 * TipTap's extended Image node stores the permanent GCS reference.
 *
 * Also normalizes image placement for TipTap's block-level Image extension.
 *
 * This keeps the PostgreSQL document payload small (just URLs, not megabytes
 * of base64) and lets the server refresh signed URLs on every load.
 */
export async function uploadDocxImagesToGCS(
  html: string,
  docId: string,
  backendUrl: string,
  workproductId: string
): Promise<string> {
  // Quick check — bail out early if there are no embedded images
  if (!html.includes("data:image/")) {
    // Still normalize image placement even if there are no base64 images
    // (e.g. images were already uploaded or are external URLs)
    return normalizeImagesForTipTap(html);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const images = doc.querySelectorAll("img");

  let imageIndex = 0;

  for (const img of Array.from(images)) {
    const src = img.getAttribute("src") || "";
    if (!src.startsWith("data:image/")) continue;

    // Parse the data URI: data:image/png;base64,XXXX...
    const match = src.match(/^data:(image\/[a-z+]+);base64,(.+)$/i);
    if (!match) continue;

    const contentType = match[1];
    const base64 = match[2];

    try {
      const resp = await fetch(
        `${backendUrl}/api/document-agent/upload-docx-image`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            base64,
            contentType,
            docId,
            workproductId,
            index: imageIndex,
          }),
        }
      );

      if (!resp.ok) {
        imageIndex++;
        continue;
      }

      const { gcsFileName, signedUrl } = await resp.json();

      // Replace the heavy base64 src with the lightweight signed URL
      img.setAttribute("src", signedUrl);
      // Store the permanent GCS reference so signed URLs can be refreshed
      img.setAttribute("data-gcs-file-name", gcsFileName);

    } catch (err) {
    }

    imageIndex++;
  }

  // Serialize back to an HTML string (body innerHTML only, no <html>/<head>)
  let result = doc.body.innerHTML;

  // Normalize image placement: ensure <img> tags are at block level,
  // not nested inside <p> tags.  This prevents TipTap's ProseMirror parser
  // from dropping content after a block-level image found in inline context.
  result = normalizeImagesForTipTap(result);

  return result;
}

/**
 * Import DOCX and return raw extracted text (no HTML)
 */
export async function importDocxToText(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}
